/**
 * Shared editable + AI-refinable outreach composer — used by both
 * DossierPage's DraftEmailDialog (wired to the real send path) and
 * SupplierDetailPage's DraftOutreachForm (copy-to-clipboard only).
 *
 * Pure presentation over a caller-owned `outreachComposerReducer` state
 * (lib/outreach.ts) — this file has no component tests per repo convention
 * (pure-helper tests only); the reducer itself is table-driven tested.
 *
 * One refine call per click: preset buttons apply a fixed instruction,
 * the free-text box applies whatever the user typed. Each call sends the
 * CURRENT edited subject/body — never the original draft — so refinements
 * compose. "Revert" restores the text from immediately before the last
 * refine (single level of undo, per the design doc's no-loops LLM rule:
 * this is one more explicit click, not automation).
 */
import { useState, type Dispatch } from 'react';
import { FormField, Input } from '../../design';
import { ApiCallError } from '../../lib/api';
import {
  OUTREACH_REFINE_PRESETS,
  useRefineOutreach,
  type OutreachComposerAction,
  type OutreachComposerState,
} from '../../lib/outreach';

export interface OutreachComposerProps {
  supplierId: string;
  state: OutreachComposerState;
  dispatch: Dispatch<OutreachComposerAction>;
  /** Disables all editing/refining — e.g. while a sibling send/mark-sent call is in flight. */
  disabled?: boolean;
  subjectId?: string;
  bodyId?: string;
}

export function OutreachComposer({
  supplierId,
  state,
  dispatch,
  disabled = false,
  subjectId = 'outreach-subject',
  bodyId = 'outreach-body',
}: OutreachComposerProps) {
  const refine = useRefineOutreach();
  const [instruction, setInstruction] = useState('');
  const busy = disabled || refine.isPending;

  async function applyInstruction(text: string) {
    const trimmed = text.trim();
    if (!trimmed) return;
    try {
      const revision = await refine.mutateAsync({
        supplierId,
        subject: state.subject,
        body: state.body,
        instruction: trimmed,
      });
      dispatch({ type: 'refined', subject: revision.subject, body: revision.body });
      setInstruction('');
    } catch {
      // surfaced via refine.error below
    }
  }

  return (
    <div className="space-y-3">
      <FormField label="Subject" htmlFor={subjectId}>
        <Input
          id={subjectId}
          value={state.subject}
          disabled={busy}
          onChange={(e) => dispatch({ type: 'edit', field: 'subject', value: e.target.value })}
        />
      </FormField>

      <FormField label="Body" htmlFor={bodyId}>
        <textarea
          id={bodyId}
          value={state.body}
          disabled={busy}
          onChange={(e) => dispatch({ type: 'edit', field: 'body', value: e.target.value })}
          rows={10}
          className="w-full rounded-sm border border-border-default bg-surface-1 px-3 py-2 text-sm font-sans text-fg-primary placeholder:text-fg-tertiary focus:outline-none focus:ring-2 focus:ring-accent-primary/40 focus:border-accent-primary/40 disabled:opacity-50 disabled:cursor-not-allowed"
        />
      </FormField>

      <div className="flex flex-wrap items-center gap-2">
        {OUTREACH_REFINE_PRESETS.map((preset) => (
          <button
            key={preset.label}
            type="button"
            disabled={busy}
            onClick={() => applyInstruction(preset.instruction)}
            className="h-8 px-3 text-sm rounded-sm border border-border-default bg-surface-elev text-fg-primary hover:bg-surface disabled:opacity-50 disabled:pointer-events-none transition-colors duration-fast"
          >
            {preset.label}
          </button>
        ))}
        {state.previous && (
          <button
            type="button"
            disabled={busy}
            onClick={() => dispatch({ type: 'revert' })}
            className="h-8 px-3 text-sm rounded-sm text-fg-secondary hover:text-fg-primary hover:bg-surface disabled:opacity-50 disabled:pointer-events-none transition-colors duration-fast"
          >
            Revert
          </button>
        )}
      </div>

      <div className="flex items-center gap-2">
        <Input
          value={instruction}
          disabled={busy}
          onChange={(e) => setInstruction(e.target.value)}
          placeholder="Custom instruction — e.g. mention we can pay net-30"
          inputSize="sm"
          className="flex-1"
        />
        <button
          type="button"
          disabled={busy || !instruction.trim()}
          onClick={() => applyInstruction(instruction)}
          className="h-8 px-3 text-sm rounded-sm bg-accent text-fg-on-accent hover:bg-accent-hover disabled:opacity-50 disabled:pointer-events-none transition-colors duration-fast"
        >
          Apply
        </button>
      </div>

      {refine.isPending && <p className="text-xs text-fg-tertiary">Refining…</p>}
      {refine.isError && (
        <p role="alert" className="text-sm text-negative">
          {(refine.error as ApiCallError)?.message ?? 'Refine failed'}
        </p>
      )}
    </div>
  );
}
